import React from "react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { RuntimeScheduleType } from "../../models/ClientConfiguration/RuntimeScheduleType";

const DAYS = [
  { value: "mon", label: "Mon", type: RuntimeScheduleType.Monday },
  { value: "tue", label: "Tue", type: RuntimeScheduleType.Tuesday },
  { value: "wed", label: "Wed", type: RuntimeScheduleType.Wednesday },
  { value: "thu", label: "Thu", type: RuntimeScheduleType.Thursday },
  { value: "fri", label: "Fri", type: RuntimeScheduleType.Friday },
  { value: "sat", label: "Sat", type: RuntimeScheduleType.Saturday },
  { value: "sun", label: "Sun", type: RuntimeScheduleType.Sunday },
] as const;

const WEEKDAY_VALUES = ["mon", "tue", "wed", "thu", "fri"];
const WEEKEND_VALUES = ["sat", "sun"];
const ALL_DAY_VALUES = [...WEEKDAY_VALUES, ...WEEKEND_VALUES];

function flagsToDayValues(flags: number): string[] {
  if (flags & RuntimeScheduleType.Daily) return [...ALL_DAY_VALUES];

  const days: string[] = [];
  if (flags & RuntimeScheduleType.WorkDays) days.push(...WEEKDAY_VALUES);
  if (flags & RuntimeScheduleType.Weekends) days.push(...WEEKEND_VALUES);

  for (const day of DAYS) {
    if ((flags & day.type) && !days.includes(day.value)) {
      days.push(day.value);
    }
  }

  return days;
}

function dayValuesToFlags(dayValues: string[]): number {
  const selected = new Set(dayValues);
  const hasAllWeekdays = WEEKDAY_VALUES.every((d) => selected.has(d));
  const hasAllWeekends = WEEKEND_VALUES.every((d) => selected.has(d));

  if (hasAllWeekdays && hasAllWeekends) return RuntimeScheduleType.Daily;

  let flags = 0;

  if (hasAllWeekdays) {
    flags |= RuntimeScheduleType.WorkDays;
  } else {
    for (const day of DAYS.slice(0, 5)) {
      if (selected.has(day.value)) flags |= day.type;
    }
  }

  if (hasAllWeekends) {
    flags |= RuntimeScheduleType.Weekends;
  } else {
    for (const day of DAYS.slice(5)) {
      if (selected.has(day.value)) flags |= day.type;
    }
  }

  return flags;
}

const RuntimeScheduleTypeSelect: React.FC<{
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled = false }) => {
  const selectedDays = flagsToDayValues(value);
  const selectedSet = new Set(selectedDays);

  const isExactMatch = (preset: string[]) =>
    preset.length === selectedDays.length && preset.every((d) => selectedSet.has(d));

  const applyPreset = (preset: string[]) => {
    onChange(isExactMatch(preset) ? 0 : dayValuesToFlags(preset));
  };

  return (
    <div className="flex items-center gap-2">
      <ToggleGroup
        type="multiple"
        value={selectedDays}
        onValueChange={(days) => onChange(dayValuesToFlags(days))}
        variant="outline"
        size="sm"
        disabled={disabled}
      >
        {DAYS.map((day) => (
          <ToggleGroupItem
            key={day.value}
            value={day.value}
            className="px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
          >
            {day.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      <Separator orientation="vertical" className="h-5" />
      <div className="flex gap-1">
        {([
          { label: "Daily", preset: ALL_DAY_VALUES },
          { label: "Workdays", preset: WEEKDAY_VALUES },
          { label: "Weekends", preset: WEEKEND_VALUES },
        ] as const).map(({ label, preset }) => {
          const active = isExactMatch(preset);
          return (
            <Button
              key={label}
              variant="outline"
              size="sm"
              className={cn("h-8 text-xs", active && "bg-primary text-primary-foreground hover:bg-primary/90")}
              onClick={() => applyPreset(preset)}
              disabled={disabled}
            >
              {label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default RuntimeScheduleTypeSelect;
